import { useEffect, useState, type FormEvent } from 'react'
import { MailCheck } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { EmailVerificationStatus } from '@/api/types'
import { useAuthStore } from '@/stores/auth'
import { trackClientEvent } from '@/lib/telemetry'
import { GateHelp } from '@/components/auth/GateHelp'
import ClaroLogo from '@/components/shared/ClaroLogo'
import { jLocale } from '@/lib/jurisdiction'

/**
 * "Verify your email" (CLA-1018).
 *
 * A self-serve signup is signed in the moment the form is submitted — the
 * email is the *second* thing they see, not the first — so proving the
 * address becomes something to close from inside the product rather than
 * a wall in front of it. This is the inside-the-product half.
 *
 * Two shapes, one form:
 *
 *   - `EmailVerificationBanner` — a strip in the shell's banner stack while
 *     the 7-day grace is running. Answers in place: the 6-digit code from
 *     the email pastes straight in, or the link in the same email does it
 *     without this banner at all. "Not now" hides it for the tab.
 *   - `EmailVerificationGate` — takes the screen once the grace has lapsed
 *     (`hard_gated`): every endpoint outside `/auth/*` is answering 403
 *     `email_unverified`, so the shell behind it would be a field of errors.
 *     Sits OUTSIDE the agreement gate, because the agreement endpoints are
 *     gated too and accepting terms into a 403 would look like a hang.
 *
 * `bounced` means the last email came back or was reported as spam; only a
 * corrected address moves forward, so the resend button gives way to the
 * change form. `expired` means no live invite — resend mints a new one.
 *
 * Every transition closes from the server's answer (`refreshEmailVerification`
 * after a success), never from assuming the 200.
 */

const DISMISS_KEY = 'claro:verify-email-dismissed'

function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function dismiss(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1')
  } catch {
    // Private mode / storage blocked — the banner just comes back next load.
  }
}

/** True for an account that still has an address to prove. */
function needsVerification(status: EmailVerificationStatus | null): status is EmailVerificationStatus {
  return !!status && !status.verified
}

function formatGateDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(jLocale(), { weekday: 'long', day: 'numeric', month: 'long' })
}

function errorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  return (err.body as { code?: string } | undefined)?.code
}

type Notice = { tone: 'ok' | 'error'; text: string }

interface FormProps {
  status: EmailVerificationStatus
  /** Compact = the banner strip; full = the gate screen and the signup step. */
  layout: 'strip' | 'full'
  /**
   * Which surface took the code, for the completion event — so the funnel can
   * tell the post-signup ask (CLA-1935) from the banner and the gate.
   */
  surface: 'banner' | 'gate' | 'signup_step'
}

/**
 * Code entry + resend + change, shared by the strip, the gate and the
 * post-signup step (`onboarding/ConfirmEmailStep`). Owns the network calls;
 * the parents only decide whether to show it.
 */
export function VerifyEmailForm({ status, layout, surface }: FormProps) {
  const refresh = useAuthStore((s) => s.refreshEmailVerification)
  const [code, setCode] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [changing, setChanging] = useState(status.state === 'bounced')
  const [busy, setBusy] = useState<'verify' | 'resend' | 'change' | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)

  // A bounce reported after mount (the store refreshed) opens the change form
  // the same way a bounce known at mount does.
  useEffect(() => {
    if (status.state === 'bounced') setChanging(true)
  }, [status.state])

  async function verify(e: FormEvent) {
    e.preventDefault()
    const trimmed = code.replace(/\s+/g, '')
    if (trimmed.length < 6 || busy) return
    setBusy('verify')
    setNotice(null)
    try {
      await api.post('/auth/email-verification/verify', { code: trimmed })
      trackClientEvent('email_verification_completed', { method: 'code', surface })
      const next = await refresh()
      if (!next?.verified) {
        // The server said 200 and then said "not verified" — surface it rather
        // than leave a banner that claims success and never goes away.
        setNotice({ tone: 'error', text: "That didn't stick — try again." })
      }
    } catch (err) {
      switch (errorCode(err)) {
        case 'too_many_attempts':
          setNotice({ tone: 'error', text: 'Too many tries with that code. Send a new one and try again.' })
          break
        case 'code_expired':
          setNotice({ tone: 'error', text: 'That code has expired. Send a new one.' })
          break
        case 'no_code':
          setNotice({ tone: 'error', text: 'No code is live for this account — send a new one.' })
          break
        default:
          setNotice({ tone: 'error', text: "That code didn't match. Check the latest email, or send a new one." })
      }
    } finally {
      setBusy(null)
    }
  }

  async function resend() {
    if (busy) return
    setBusy('resend')
    setNotice(null)
    try {
      const res = await api.post<{ message?: string }>('/auth/email-verification/resend')
      setNotice({ tone: 'ok', text: res.message || 'Sent — give it a minute, and check junk.' })
      setCode('')
      void refresh()
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : ''
      if (errorCode(err) === 'bounced') {
        setChanging(true)
        setNotice({ tone: 'error', text: detail || 'Email to that address is bouncing. Correct the address to continue.' })
      } else {
        // too_soon / daily_cap carry their own sentence; anything else falls
        // back to something that names the next move.
        setNotice({ tone: 'error', text: detail || "That didn't send — try again in a minute." })
      }
    } finally {
      setBusy(null)
    }
  }

  async function change(e: FormEvent) {
    e.preventDefault()
    const email = newEmail.trim()
    if (!email || busy) return
    setBusy('change')
    setNotice(null)
    try {
      const res = await api.post<{ message?: string }>('/auth/email-verification/change', { email })
      setNotice({ tone: 'ok', text: res.message || `Updated — we've sent a new code to ${email}.` })
      setChanging(false)
      setCode('')
      setNewEmail('')
      void refresh()
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : ''
      setNotice({ tone: 'error', text: detail || "That didn't save — check the address and try again." })
    } finally {
      setBusy(null)
    }
  }

  const full = layout === 'full'
  const inputBase =
    'text-sm text-ink bg-surface border border-border-input rounded-lg px-2.5 py-1.5 outline-none disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-claro-600'
  const primary =
    'shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed'
  const quiet = 'shrink-0 px-2 py-1.5 text-xs font-medium text-ink-secondary hover:text-ink transition-colors disabled:opacity-60'

  return (
    <div className={full ? 'space-y-4' : 'flex items-center gap-2 flex-wrap'}>
      {changing ? (
        <form onSubmit={(e) => void change(e)} className="flex items-center gap-2 flex-wrap">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Correct email address"
            aria-label="Correct email address"
            autoComplete="email"
            disabled={busy !== null}
            className={`${inputBase} min-w-[220px]`}
          />
          <button type="submit" disabled={!newEmail.trim() || busy !== null} className={primary}>
            {busy === 'change' ? 'Updating…' : 'Update and resend'}
          </button>
          {status.state !== 'bounced' && (
            <button type="button" onClick={() => setChanging(false)} disabled={busy !== null} className={quiet}>
              Cancel
            </button>
          )}
        </form>
      ) : (
        <form onSubmit={(e) => void verify(e)} className="flex items-center gap-2 flex-wrap">
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, ''))}
            placeholder="6-digit code"
            aria-label="6-digit code from the email"
            disabled={busy !== null}
            className={`${inputBase} w-[7.5rem] tracking-widest`}
          />
          <button type="submit" disabled={code.replace(/\s+/g, '').length < 6 || busy !== null} className={primary}>
            {busy === 'verify' ? 'Checking…' : 'Verify'}
          </button>
          <button type="button" onClick={() => void resend()} disabled={busy !== null} className={quiet}>
            {busy === 'resend' ? 'Sending…' : status.state === 'expired' ? 'Send a new code' : 'Resend'}
          </button>
          <button type="button" onClick={() => setChanging(true)} disabled={busy !== null} className={quiet}>
            Wrong address?
          </button>
        </form>
      )}
      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`text-xs ${notice.tone === 'error' ? 'text-on-error-soft' : 'text-ink-secondary'} ${full ? '' : 'basis-full'}`}
        >
          {notice.text}
        </p>
      )}
    </div>
  )
}

function headline(status: EmailVerificationStatus): string {
  switch (status.state) {
    case 'bounced':
      return `Email to ${status.email ?? 'that address'} is bouncing.`
    case 'expired':
      return 'Your verification link has expired.'
    default:
      return `Check your email — a link and a code went to ${status.email ?? 'your address'}.`
  }
}

export function EmailVerificationBanner() {
  const status = useAuthStore((s) => s.emailVerification)
  const [hidden, setHidden] = useState(isDismissed)

  if (!needsVerification(status) || status.hard_gated || hidden) return null

  const gateDate = formatGateDate(status.hard_gate_at)

  return (
    <div className="bg-claro-500/10 border-b border-claro-500/25 px-4 py-2 flex items-start gap-3 flex-wrap shrink-0">
      <MailCheck className="w-4 h-4 text-claro-700 shrink-0 mt-0.5" aria-hidden />
      <p className="flex-1 min-w-[240px] text-[13px] text-ink-secondary">
        <span className="font-semibold text-ink">{headline(status)}</span>{' '}
        {status.state === 'bounced'
          ? 'Enter the right address and a new code will follow.'
          : gateDate
            ? `Click the link or enter the code by ${gateDate} to keep using Claro.`
            : 'Click the link or enter the code to confirm it is yours.'}
      </p>
      <VerifyEmailForm status={status} layout="strip" surface="banner" />
      <button
        onClick={() => {
          dismiss()
          setHidden(true)
        }}
        className="shrink-0 px-2 py-1.5 text-xs font-medium text-ink-secondary hover:text-ink transition-colors"
      >
        Not now
      </button>
    </div>
  )
}

/**
 * Full-screen stop once the grace period has lapsed. Wraps both shell
 * branches (wizard and app) in AppShell; a no-op for anyone verified or
 * still inside the grace.
 *
 * Also listens for a 403 `email_unverified` from any call — a session that
 * crosses the 7-day line while open would otherwise see the shell fail one
 * request at a time. The client dispatches `claro:email-unverified`; this
 * re-reads the status, which is what flips `hard_gated` and takes the screen.
 */
export function EmailVerificationGate({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.emailVerification)
  const refresh = useAuthStore((s) => s.refreshEmailVerification)

  useEffect(() => {
    const onUnverified = () => void refresh()
    window.addEventListener('claro:email-unverified', onUnverified)
    return () => window.removeEventListener('claro:email-unverified', onUnverified)
  }, [refresh])

  if (!needsVerification(status) || !status.hard_gated) return <>{children}</>

  return (
    <div className="flex-1 flex items-center justify-center min-h-dvh w-full px-4 bg-surface">
      <div className="w-full max-w-md space-y-5">
        <ClaroLogo size={20} />
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Confirm your email to continue</h1>
          <p className="text-sm text-ink-secondary">
            {status.state === 'bounced'
              ? `Email to ${status.email ?? 'that address'} is bouncing, so we can't reach you there. Enter the right address and a new code will follow.`
              : `Your account has been open for a week without ${status.email ?? 'your address'} being confirmed. Click the link in the email, or enter the code from it here — everything is exactly where you left it.`}
          </p>
        </div>
        <VerifyEmailForm status={status} layout="full" surface="gate" />
        {/* CLA-1390: the shell's feedback widget is behind this gate. */}
        <GateHelp
          gate="email_unverified"
          placeholder="If we can't reach you at this address, include a phone number or another email."
        />
      </div>
    </div>
  )
}
